import { useState, useEffect, useMemo } from 'react'
import { PLATE_BOT_NAME, PLATE_BOT_AVATAR } from '@/const/constants'
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom'
import { taskPath } from '@/utils/taskPath'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { markAllWatchedSaga, markNotificationWatchedSaga } from '../../../store/notifications/notificationsSlice'
import { getNotificationTitle } from '@/utils/notificationTitle'
import { showToast } from '@/components/app/Toast/showToast'
import {
    selectAllNotificationsFull,
    selectNotificationsLoadingFull
} from '../../../store/notifications/notificationsSelectors'
import type { Notification } from '../../../store/notifications/notificationsSlice'
import { selectTasksById, selectTasksInitialized, selectTasksCompletedInitialized } from '../../../store/tasks/tasksSelectors'
import { selectProjectsById, selectProjectsLoading } from '../../../store/projects/projectsSelectors'
import { subscribeWorkspaceActivitySaga, unsubscribeWorkspaceActivitySaga, loadMoreWorkspaceActivitySaga } from '../../../store/activity/activitySlice'
import { getActivityActionText, groupByDay } from '../../../store/activity/activityUtils'
import { getActivityActorDisplay } from '@/utils/activityActorDisplay'
import type { ActivityEvent } from '../../../store/activity/activitySlice'
import {
    selectWorkspaceActivity,
    selectWorkspaceActivityLoading,
    selectWorkspaceActivityHasMore,
} from '../../../store/activity/activitySelectors'
import { selectListById } from '../../../store/lists/listsSelectors'
import NotificationDot from '../../ui/NotificationDot/NotificationDot'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import IconButton from '@/components/ui/IconButton/IconButton'
import Button from '@/components/ui/Button/Button'
import OptionsIcon from '../../../assets/icons/icon-options.svg?react'
import InboxPointIcon from '../../../assets/icons/icon-inbox-point.svg?react'
import { TextH2, TextMidd, TextNorm } from '../../ui/Text'
import { useRelativeTime } from '../../../hooks/useRelativeTime'
import Avatar from '../../ui/Avatar/Avatar'
import ActivitySourceBadge from '@/components/app/ActivitySourceBadge/ActivitySourceBadge'
import Empty from '../../ui/Empty/Empty'
import PlateLoader from '../../ui/Loader/PlateLoader'
import css from './WorkspaceInboxPage.module.css'
import { setOpenModal } from '../../../store/ui/uiSlice'

type InboxTab = 'notifications' | 'activity'

// ─── Activity item ────────────────────────────────────────────────────────────

interface ActivityItemProps {
    event: ActivityEvent
}

const ActivityItem = ({ event }: ActivityItemProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const { getMember, statuses, workspace } = useWorkspace()
    const tasksById = useAppSelector(selectTasksById)
    const fromListName = useAppSelector(state =>
        event.meta.fromListId ? selectListById(state, event.meta.fromListId)?.name : undefined
    )
    const toListName = useAppSelector(state =>
        event.meta.toListId ? selectListById(state, event.meta.toListId)?.name : undefined
    )
    const formatRelativeTime = useRelativeTime()

    const { displayName, avatarColor, avatarImage } = getActivityActorDisplay(event, getMember)

    const actionText = getActivityActionText(event, { statuses, getMember, fromListName, toListName })

    const task = event.taskId ? tasksById[event.taskId] : undefined
    const isProjectEvent = event.type === 'project_created' || event.type === 'project_deleted'
    const taskLabel = isProjectEvent ? null
        : task
            ? `${workspace?.workspaceTaskPrefixId}-${task.number} ${task.name}`
            : (event.meta.taskName
                ? `${workspace?.workspaceTaskPrefixId}-${event.meta.taskNumber} ${event.meta.taskName}`
                : null)

    const handleClick = () => {
        if (!task || !workspaceUrlId || !workspace) return
        const path = taskPath({ workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: task.number, pending: task.pending })
        dispatch(setOpenModal(null))
        if (path) navigate(path, { state: { backgroundLocation: location } })
    }

    return (
        <div className={css.item} onClick={task ? handleClick : undefined} style={!task ? { cursor: 'default' } : undefined}>
            <div className={css.avatar}>
                <Avatar title={displayName} color={avatarColor} avatar={avatarImage} size="medium2" />
            </div>
            <div className={css.content}>
                {taskLabel && (
                    <span className={css.task_name}><TextNorm>{taskLabel}</TextNorm></span>
                )}
                <div className={css.meta}>
                    <span className={css.meta_name}>
                        <TextMidd>{displayName}<ActivitySourceBadge event={event} /> {actionText}</TextMidd>
                    </span>
                    {event.createdAt && <InboxPointIcon className={css.meta_dot} />}
                    {event.createdAt && (
                        <span className={css.meta_date}>
                            <TextMidd>{formatRelativeTime(event.createdAt)}</TextMidd>
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const WorkspaceInboxPage = () => {
    const dispatch = useAppDispatch()
    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { workspace, getMember, statuses } = useWorkspace()
    const currentUserId = useAppSelector(state => state.auth?.id)
    const globalLoading = useAppSelector(state => state.ui.globalLoading)
    const notifications = useAppSelector(selectAllNotificationsFull)
    const notificationsLoading = useAppSelector(selectNotificationsLoadingFull)
    const tasksById = useAppSelector(selectTasksById)
    const tasksInitialized = useAppSelector(selectTasksInitialized)
    const tasksCompletedInitialized = useAppSelector(selectTasksCompletedInitialized)
    const projectsById = useAppSelector(selectProjectsById)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const workspaceActivity = useAppSelector(selectWorkspaceActivity)
    const activityLoading = useAppSelector(selectWorkspaceActivityLoading)
    const activityHasMore = useAppSelector(selectWorkspaceActivityHasMore)
    const formatRelativeTime = useRelativeTime()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<InboxTab>('notifications')
    const [activitySubscribed, setActivitySubscribed] = useState(false)
    const [watchedLimit, setWatchedLimit] = useState(50)

    const activityGroups = useMemo(() => groupByDay(workspaceActivity), [workspaceActivity])

    useEffect(() => {
        if (activeTab === 'activity' && !activitySubscribed && workspace?.id) {
            dispatch(subscribeWorkspaceActivitySaga(workspace.id))
            setActivitySubscribed(true)
        }
    }, [activeTab, activitySubscribed, workspace?.id, dispatch])

    useEffect(() => {
        return () => { dispatch(unsubscribeWorkspaceActivitySaga()) }
    }, [dispatch])

    if (globalLoading || notificationsLoading) {
        return <PlateLoader fullscreen />
    }

    if (!currentUserId) {
        return <Navigate to="/" />
    }

    const handleMarkAllRead = () => {
        if (!workspace?.id || !currentUserId) return
        dispatch(markAllWatchedSaga({ userId: currentUserId, workspaceId: workspace.id }))
        setIsDropdownOpen(false)
    }

    const tasksLoaded = tasksInitialized && tasksCompletedInitialized

    // A notification is normally marked read by opening its task (TaskModal) or project.
    // When the target is gone there's nothing to open, so mark it read right here —
    // otherwise the notification and the unread dot stay forever.
    const clearOrphanNotification = (n: Notification, text: string) => {
        dispatch(markNotificationWatchedSaga(n.id))
        showToast(text)
    }

    const handleNotificationClick = (n: Notification) => {
        if (!workspace) return
        if (n.taskId) {
            const task = tasksById[n.taskId]
            if (!task) {
                if (tasksLoaded) clearOrphanNotification(n, 'This task no longer exists.')
                return
            }
            const path = taskPath({ workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: task.number, pending: task.pending })
            if (!path) return
            navigate(path, { state: { backgroundLocation: location } })
        } else if (n.projectId) {
            if (!projectsById[n.projectId]) {
                if (!projectsLoading) clearOrphanNotification(n, 'This project no longer exists.')
                return
            }
            navigate(`/${workspaceUrlId}/projects/${n.projectId}`)
        }
    }

    const getActionText = (n: Notification) => {
        if (n.type === 'comment') return 'replied'
        if (n.type === 'assignee') return 'assigned to you'
        if (n.type === 'mention') return 'mentioned you'
        if (n.type === 'status') {
            const task = tasksById[n.taskId]
            const status = task ? statuses.find(s => s.id === task.statusId) : null
            return status ? `marked as ${status.name}` : 'changed status'
        }
        return n.type
    }

    const renderNotification = (n: Notification) => {
        const name = getNotificationTitle({
            notification: n,
            task: tasksById[n.taskId],
            project: projectsById[n.projectId],
            tasksLoaded,
        })
        const initiator = getMember(n.createdBy) ?? (n.createdBy === null ? {
            username: PLATE_BOT_NAME,
            color: '#6366f1',
            avatarImage: PLATE_BOT_AVATAR,
        } as any : null)
        const actionText = getActionText(n)
        return (
            <div
                key={n.id}
                className={css.item}
                onClick={() => handleNotificationClick(n)}
            >
                <div className={css.avatar}>
                    {initiator && (
                        <Avatar
                            size="medium2"
                            title={initiator.username}
                            color={initiator.color}
                            avatar={initiator.avatarImage}
                        />
                    )}
                </div>
                <div className={css.content}>
                    <span className={css.task_name}><TextNorm>{name}</TextNorm></span>
                    <div className={css.meta}>
                        {initiator && <span className={css.meta_name}><TextMidd>{initiator.username} {actionText}</TextMidd></span>}
                        {n.createdAt && <InboxPointIcon className={css.meta_dot} />}
                        {n.createdAt && <span className={css.meta_date}><TextMidd>{formatRelativeTime(n.createdAt)}</TextMidd></span>}
                    </div>
                </div>
                {!n.watched && <NotificationDot />}
            </div>
        )
    }

    const unwatched = notifications.filter((n: Notification) => !n.watched)
    const watched = notifications
        .filter((n: Notification) => n.watched)
        .sort((a: Notification, b: Notification) => {
            const timeA = a.watchedAt?.seconds ?? 0
            const timeB = b.watchedAt?.seconds ?? 0
            return timeB - timeA
        })

    return (
        <div className={css.container}>
            <div className={css.header_top_right}>
                {activeTab === 'notifications' && (
                    <DropdownWrapper>
                        <IconButton grey={true} icon={<OptionsIcon />} onClick={() => setIsDropdownOpen(!isDropdownOpen)} />
                        <Dropdown align="right" isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
                            <DropdownItem title="Mark all as read" onClick={handleMarkAllRead} />
                        </Dropdown>
                    </DropdownWrapper>
                )}
            </div>

            <div className={css.tabs}>
                <button
                    className={[css.tab, activeTab === 'notifications' ? css.tab_active : ''].filter(Boolean).join(' ')}
                    onClick={() => setActiveTab('notifications')}
                >
                    <TextMidd>Notifications</TextMidd>
                </button>
                <button
                    className={[css.tab, activeTab === 'activity' ? css.tab_active : ''].filter(Boolean).join(' ')}
                    onClick={() => setActiveTab('activity')}
                >
                    <TextMidd>Activity</TextMidd>
                </button>
            </div>

            {activeTab === 'notifications' && (
                notifications.length === 0 ? (
                    <Empty title="Inbox" text="There's nothing here." />
                ) : (
                    <>
                        {unwatched.length > 0 && (
                            <div className={css.section}>
                                <div className={css.section_title}><TextH2>New to you</TextH2></div>
                                <div className={css.list}>{unwatched.map(renderNotification)}</div>
                            </div>
                        )}
                        {watched.length > 0 && (
                            <div className={css.section}>
                                <div className={css.section_title}><TextH2>Past notifications</TextH2></div>
                                <div className={css.list}>{watched.slice(0, watchedLimit).map(renderNotification)}</div>
                                {watched.length > watchedLimit && (
                                    <div className={css.load_more}>
                                        <Button
                                            title="Load more"
                                            type="light"
                                            onClick={() => setWatchedLimit(prev => prev + 50)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )
            )}

            {activeTab === 'activity' && (
                activityLoading && workspaceActivity.length === 0 ? (
                    <div className={css.section} />
                ) : workspaceActivity.length === 0 ? (
                    <Empty title="Activity" text="No activity yet." />
                ) : (
                    <div className={css.activity_groups}>
                        {activityGroups.map(group => (
                            <div key={group.label} className={css.section}>
                                <div className={css.section_title}><TextH2>{group.label}</TextH2></div>
                                <div className={css.list}>
                                    {group.events.map((event: ActivityEvent) => (
                                        <ActivityItem key={event.id} event={event} />
                                    ))}
                                </div>
                            </div>
                        ))}
                        {activityHasMore && (
                            <div className={css.load_more}>
                                <Button
                                    title="Load more"
                                    type="light"
                                    loading={activityLoading}
                                    onClick={() => dispatch(loadMoreWorkspaceActivitySaga())}
                                />
                            </div>
                        )}
                    </div>
                )
            )}
        </div>
    )
}

export default WorkspaceInboxPage
